import { filter, find, first, toLower } from "lodash";
import { Person } from "@/features/people/graphql/generated";
import { DimensionWithScores } from "@/features/people";
import { ScorecardRow } from "@/features/scorecards/graphql/generated";
import { TFunction } from "i18next";
import { CandidateCompetencyInsight } from "./useCandidateCompetencyInsights";

/**
 * Parameters specific to candidate competency insights
 */
export interface CandidateCompetencyInsightParams {
  person?: Person;
  defaultPersonId?: number;
  scorecardRows?: ScorecardRow[];
  interviewQuestions?: string[];
}

/**
 * Helper to check if a dimension has a scorecard row
 */
function hasScorecardRow(
  dimension: DimensionWithScores,
  scorecardRows?: ScorecardRow[]
): boolean {
  if (!scorecardRows) return false;
  return scorecardRows.some(
    (row) =>
      dimension.id === first(row.dimensions as { dim_id: number }[])?.dim_id
  );
}

/**
 * Helper to get scorecard row for a dimension
 */
function getScorecardRow(
  dimension: DimensionWithScores,
  scorecardRows?: ScorecardRow[]
): ScorecardRow | undefined {
  if (!scorecardRows) return undefined;
  return find(
    scorecardRows,
    (row) =>
      dimension.id === first(row.dimensions as { dim_id: number }[])?.dim_id
  );
}

/**
 * Creates an interview focus insight for a single dimension (score <= 35 with scorecard row)
 * Returns null if dimension doesn't qualify for an interview focus insight
 */
export function getInterviewFocusInsight(
  dimension: DimensionWithScores,
  params: CandidateCompetencyInsightParams,
  t: TFunction
): CandidateCompetencyInsight | null {
  const score = dimension.score?.value || 0;

  // Must be low confidence (<= 35)
  if (score > 35) {
    return null;
  }

  // Must have parentId (child dimension)
  if (dimension.parentId === null) {
    return null;
  }

  const { scorecardRows, interviewQuestions, person } = params;

  // Must have a scorecard row
  if (!hasScorecardRow(dimension, scorecardRows)) {
    return null;
  }

  const scorecardRow = getScorecardRow(dimension, scorecardRows);

  return {
    dimension,
    type: "interview_focus",
    title: t("competency_insights.interview_focus_title", {
      dimensionName: dimension.title,
      context: person?.first_name,
    }),
    subtitle: "",
    description: t("competency_insights.interview_focus_description", {
      dimensionName: toLower(dimension.title),
      context: person?.first_name,
    }),
    highlight: {
      color: "bg-blue-100",
      title: t("competency_insights.interview_questions_highlight_title", {
        dimensionName: dimension.title,
        context: person?.first_name,
      }),
      description: t(
        "competency_insights.interview_questions_highlight_description",
        {
          context: person?.first_name,
          dimensionName: dimension.title,
        }
      ),
      questions: interviewQuestions || [],
      tag: {
        label: t("competency_insights.interview_focus"),
        color: "text-blue-500",
        icon: "Conversation",
        bg: "bg-blue-500",
      },
    },
    tags: [
      {
        label: scorecardRow?.group_title || "",
        color: "text-blue-500",
        icon: "Warning",
        bg: "bg-blue-500",
      },
      {
        label: t("competency_insights.low_confidence"),
        color: "text-orange-500",
        icon: "Lowest",
        bg: "bg-orange-500",
      },
    ],
    people: person ? [person] : [],
  };
}

/**
 * Creates a likely strength insight for a single candidate dimension (score > 65 with scorecard row)
 * Returns null if dimension doesn't qualify for a likely strength insight
 */
export function getCandidateLikelyStrengthInsight(
  dimension: DimensionWithScores,
  params: CandidateCompetencyInsightParams,
  t: TFunction
): CandidateCompetencyInsight | null {
  const score = dimension.score?.value || 0;

  // Must be high confidence (> 65)
  if (score <= 65) {
    return null;
  }

  // Must have parentId (child dimension)
  if (dimension.parentId === null) {
    return null;
  }

  const { scorecardRows, person } = params;

  // Must have a scorecard row
  if (!hasScorecardRow(dimension, scorecardRows)) {
    return null;
  }

  return {
    dimension,
    type: "likely_strengths",
    title: t("competency_insights.likely_strengths_title", {
      dimensionName: dimension.title,
    }),
    subtitle: "",
    description: t("competency_insights.likely_strengths_description", {
      dimensionName: dimension.title,
      context: person?.first_name,
    }),
    highlight: {
      color: "bg-green-100",
      title: t("competency_insights.likely_strengths_highlight_title", {
        dimensionName: dimension.title,
        context: person?.first_name,
      }),
      description: t(
        "competency_insights.likely_strengths_highlight_description",
        {
          context: person?.first_name,
          dimensionName: dimension.title,
        }
      ),
      tag: {
        label: t("competency_insights.likely_strengths"),
        color: "text-green-500",
        icon: "Strength",
        bg: "bg-green-500",
      },
    },
    tags: [
      {
        label: t("competency_insights.likely_strengths"),
        color: "text-green-500",
        icon: "Strength",
        bg: "bg-green-500",
      },
    ],
    people: person ? [person] : [],
  };
}

/**
 * Creates a development potential insight for a single candidate dimension (35 < score <= 65 with scorecard row)
 * Returns null if dimension doesn't qualify for a development potential insight
 */
export function getCandidateDevelopmentPotentialInsight(
  dimension: DimensionWithScores,
  params: CandidateCompetencyInsightParams,
  t: TFunction
): CandidateCompetencyInsight | null {
  const score = dimension.score?.value || 0;

  // Must be neutral (35 < score <= 65)
  if (score <= 35 || score > 65) {
    return null;
  }

  // Must have parentId (child dimension)
  if (dimension.parentId === null) {
    return null;
  }

  const { scorecardRows, person } = params;

  // Must have a scorecard row
  if (!hasScorecardRow(dimension, scorecardRows)) {
    return null;
  }

  return {
    dimension,
    type: "development_potential",
    title: t("competency_insights.development_potential_title", {
      dimensionName: dimension.title,
    }),
    subtitle: "",
    description: t("competency_insights.development_potential_description", {
      dimensionName: dimension.title,
      context: person?.first_name,
    }),
    highlight: {
      color: "bg-orange-100",
      title: t("competency_insights.development_potential_highlight_title", {
        dimensionName: dimension.title,
        context: person?.first_name,
      }),
      description: t(
        "competency_insights.development_potential_highlight_description",
        {
          context: person?.first_name,
          dimensionName: dimension.title,
        }
      ),
      tag: {
        label: t("competency_insights.development_potential"),
        color: "text-orange-500",
        icon: "Plant",
        bg: "bg-orange-500",
      },
    },
    tags: [
      {
        label: t("competency_insights.development_potential"),
        color: "text-orange-500",
        icon: "Plant",
        bg: "bg-orange-500",
      },
    ],
    people: person ? [person] : [],
  };
}

/**
 * Returns a single insight for a candidate competency dimension
 * Automatically determines whether to return interview focus, likely strength, or development potential
 * Returns null if dimension doesn't qualify for any insight
 */
export function getCandidateCompetencyInsight(
  dimension: DimensionWithScores,
  params: CandidateCompetencyInsightParams,
  t: TFunction
): CandidateCompetencyInsight | null {
  // Try interview focus first (low confidence)
  const interviewFocusInsight = getInterviewFocusInsight(dimension, params, t);
  if (interviewFocusInsight) {
    return interviewFocusInsight;
  }

  // Try likely strength (high confidence)
  const likelyStrengthInsight = getCandidateLikelyStrengthInsight(
    dimension,
    params,
    t
  );
  if (likelyStrengthInsight) {
    return likelyStrengthInsight;
  }

  // Try development potential (neutral)
  const developmentPotentialInsight = getCandidateDevelopmentPotentialInsight(
    dimension,
    params,
    t
  );
  if (developmentPotentialInsight) {
    return developmentPotentialInsight;
  }

  return null;
}
